import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Users, DollarSign, TrendingUp, Settings, Rocket, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Goals = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [goals, setGoals] = useState({
    memberTarget: 100,
    consultingRevenueTarget: 100000
  });
  const [metrics, setMetrics] = useState({
    activeLeads: 0,
    proposedCards: 0,
    closedMembers: 0,
    leadToProposalRate: 0,
    proposalToCloseRate: 0,
    consultingLeads: 0,
    consultingProposals: 0,
    consultingRevenue: 0,
    consultingCloseRate: 0
  });

  useEffect(() => {
    fetchGoalsData();
  }, []);

  const fetchGoalsData = async () => {
    try {
      const [settingsRes, metricsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/goals/settings`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/goals/metrics`, { credentials: 'include' })
      ]);

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setGoals({
          memberTarget: settingsData.member_target || 100,
          consultingRevenueTarget: settingsData.consulting_revenue_target || 100000
        });
      }

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }
    } catch (err) {
      console.error('Failed to fetch goals data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveGoals = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/goals/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          member_target: goals.memberTarget,
          consulting_revenue_target: goals.consultingRevenueTarget
        })
      });
      setShowSettings(false);
      fetchGoalsData();
    } catch (err) {
      console.error('Failed to save goals:', err);
    }
  };

  // Calculate funnel metrics
  const leadsNeededForTarget = metrics.leadToProposalRate > 0 && metrics.proposalToCloseRate > 0
    ? Math.ceil(goals.memberTarget / (metrics.leadToProposalRate * metrics.proposalToCloseRate))
    : 0;
  
  const proposalsNeededForTarget = metrics.proposalToCloseRate > 0
    ? Math.ceil(goals.memberTarget / metrics.proposalToCloseRate)
    : 0;

  const leadsPercentOfTarget = leadsNeededForTarget > 0
    ? Math.min(100, Math.round((metrics.activeLeads / leadsNeededForTarget) * 100))
    : 0;

  const proposalsPercentOfTarget = proposalsNeededForTarget > 0
    ? Math.min(100, Math.round((metrics.proposedCards / proposalsNeededForTarget) * 100))
    : 0;

  const membersPercentOfTarget = goals.memberTarget > 0
    ? Math.min(100, Math.round((metrics.closedMembers / goals.memberTarget) * 100))
    : 0;

  const consultingRevenuePercent = goals.consultingRevenueTarget > 0
    ? Math.min(100, Math.round((metrics.consultingRevenue / goals.consultingRevenueTarget) * 100))
    : 0;

  return (
    <AppLayout>
      {/* Float animation for header */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .float-animation {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-3 mb-4">
            <Rocket className="w-10 h-10 text-[#A2FF00] float-animation" />
            <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Path to {goals.memberTarget} Members
            </h1>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <Settings className="w-5 h-5 text-gray-400 hover:text-white" />
            </button>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            <Sparkles className="inline w-5 h-5 text-yellow-400 mr-2" />
            Together we reach for the stars. With teamwork, dedication, and focus, 
            every goal is within reach. Let us make 2026 extraordinary!
            <Sparkles className="inline w-5 h-5 text-yellow-400 ml-2" />
          </p>
        </div>

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-200'} border rounded-lg p-6 w-full max-w-md`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Goal Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Member Target (2026)</label>
                  <input
                    type="number"
                    value={goals.memberTarget}
                    onChange={(e) => setGoals({ ...goals, memberTarget: parseInt(e.target.value) || 0 })}
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border rounded px-3 py-2`}
                  />
                </div>
                <div>
                  <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Consulting Revenue Target (£)</label>
                  <input
                    type="number"
                    value={goals.consultingRevenueTarget}
                    onChange={(e) => setGoals({ ...goals, consultingRevenueTarget: parseInt(e.target.value) || 0 })}
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border rounded px-3 py-2`}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className={`flex-1 px-4 py-2 border ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} rounded`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveGoals}
                  className="flex-1 px-4 py-2 bg-[#A2FF00] text-black font-bold rounded hover:bg-[#8EFF00]"
                >
                  Save Goals
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Funnels Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT FUNNEL - Membership Path */}
          <div className={`${isDark ? 'bg-black/40 backdrop-blur-sm border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-6`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#A2FF00]/20 rounded-xl">
                <Users className="w-6 h-6 text-[#A2FF00]" />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Membership Funnel</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Path to {goals.memberTarget} Members</p>
              </div>
            </div>

            {/* Funnel Visualization */}
            <div className="relative">
              {/* Top of Funnel - Active Leads */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-blue-600/80 to-blue-400/80 rounded-t-xl flex items-center justify-center"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white">{metrics.activeLeads}</div>
                      <div className="text-sm text-blue-100">Active Leads</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className="text-3xl font-bold text-[#A2FF00]">{leadsPercentOfTarget}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>of leads needed</div>
                  <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2 mt-2`}>
                    <div 
                      className="bg-[#A2FF00] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${leadsPercentOfTarget}%` }}
                    />
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>Need ~{leadsNeededForTarget} leads</div>
                </div>
              </div>

              {/* Middle of Funnel - Proposals */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 px-4">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-purple-600/80 to-purple-400/80 flex items-center justify-center"
                    style={{ clipPath: 'polygon(5% 0, 95% 0, 90% 100%, 10% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white">{metrics.proposedCards}</div>
                      <div className="text-sm text-purple-100">Proposals Sent</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className="text-3xl font-bold text-[#A2FF00]">{proposalsPercentOfTarget}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>of proposals needed</div>
                  <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2 mt-2`}>
                    <div 
                      className="bg-[#A2FF00] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${proposalsPercentOfTarget}%` }}
                    />
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>Need ~{proposalsNeededForTarget} proposals</div>
                </div>
              </div>

              {/* Bottom of Funnel - Members */}
              <div className="flex items-center gap-4">
                <div className="flex-1 px-8">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-[#A2FF00]/80 to-green-400/80 rounded-b-xl flex items-center justify-center"
                    style={{ clipPath: 'polygon(10% 0, 90% 0, 80% 100%, 20% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-4xl font-bold text-black">{metrics.closedMembers}</div>
                      <div className="text-sm text-black/70">Members</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className="text-3xl font-bold text-[#A2FF00]">{membersPercentOfTarget}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>of target achieved</div>
                  <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2 mt-2`}>
                    <div 
                      className="bg-[#A2FF00] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${membersPercentOfTarget}%` }}
                    />
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>Target: {goals.memberTarget} members</div>
                </div>
              </div>
            </div>

            {/* Conversion Rates */}
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-3`}>Conversion Rates (90-day rolling)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-lg p-3`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{(metrics.leadToProposalRate * 100).toFixed(1)}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Lead → Proposal</div>
                </div>
                <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-lg p-3`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{(metrics.proposalToCloseRate * 100).toFixed(1)}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Proposal → Member</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT FUNNEL - Consulting Revenue */}
          <div className={`${isDark ? 'bg-black/40 backdrop-blur-sm border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-6`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <DollarSign className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Consulting Revenue</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Path to £{goals.consultingRevenueTarget.toLocaleString()}</p>
              </div>
            </div>

            {/* Consulting Funnel */}
            <div className="relative">
              {/* Top - Consulting Leads */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-orange-600/80 to-orange-400/80 rounded-t-xl flex items-center justify-center"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white">{metrics.consultingLeads}</div>
                      <div className="text-sm text-orange-100">Consulting Leads</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Potential pipeline</div>
                </div>
              </div>

              {/* Middle - Consulting Proposals */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 px-4">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-amber-600/80 to-amber-400/80 flex items-center justify-center"
                    style={{ clipPath: 'polygon(5% 0, 95% 0, 90% 100%, 10% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white">{metrics.consultingProposals}</div>
                      <div className="text-sm text-amber-100">Proposals Sent</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {(metrics.consultingCloseRate * 100).toFixed(0)}% close rate
                  </div>
                </div>
              </div>

              {/* Bottom - Revenue */}
              <div className="flex items-center gap-4">
                <div className="flex-1 px-8">
                  <div 
                    className="relative h-24 bg-gradient-to-r from-yellow-500/80 to-yellow-300/80 rounded-b-xl flex items-center justify-center"
                    style={{ clipPath: 'polygon(10% 0, 90% 0, 80% 100%, 20% 100%)' }}
                  >
                    <div className="text-center">
                      <div className="text-3xl font-bold text-black">£{metrics.consultingRevenue.toLocaleString()}</div>
                      <div className="text-sm text-black/70">Revenue</div>
                    </div>
                  </div>
                </div>
                <div className="w-40 text-right">
                  <div className="text-3xl font-bold text-yellow-400">{consultingRevenuePercent}%</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>of target achieved</div>
                  <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2 mt-2`}>
                    <div 
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${consultingRevenuePercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-3`}>Revenue Progress</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Achieved</span>
                  <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>£{metrics.consultingRevenue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Remaining</span>
                  <span className={`text-xl font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    £{Math.max(0, goals.consultingRevenueTarget - metrics.consultingRevenue).toLocaleString()}
                  </span>
                </div>
                <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-4`}>
                  <div 
                    className="bg-gradient-to-r from-yellow-500 to-yellow-300 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${consultingRevenuePercent}%` }}
                  >
                    {consultingRevenuePercent > 10 && (
                      <span className="text-xs font-bold text-black">{consultingRevenuePercent}%</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Rolling Totals */}
        <div className={`${isDark ? 'bg-black/40 backdrop-blur-sm border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-6`}>
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-[#A2FF00]" />
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Monthly Progress (Cumulative)</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-4 text-center`}>
              <div className="text-3xl font-bold text-blue-400">{metrics.activeLeads}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Total Active Leads</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-4 text-center`}>
              <div className="text-3xl font-bold text-purple-400">{metrics.proposedCards}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Total Proposals</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-4 text-center`}>
              <div className="text-3xl font-bold text-[#A2FF00]">{metrics.closedMembers}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Total Members</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-4 text-center`}>
              <div className="text-3xl font-bold text-yellow-400">£{metrics.consultingRevenue.toLocaleString()}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Consulting Revenue</div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Goals;
